import "../styles/Shop.css";
import { useContext, useEffect, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Spin } from "antd";
import { FaRegHeart, FaHeart } from "react-icons/fa";

import {
  getCategoryProductList,
  getMenu,
  getSubcategoryDetails,
} from "../services/apiService";
import {
  AddItemToCart,
  AddItemToWishList,
  DeleteFromCart,
  getProductQty,
  DeleteFromWishList,
  getShopProfession,
} from "../services/Cart";

import noimage from "../assets/noimage.webp";

import { CartContext } from "../contextApi/CartContext";
import { showSnackbar } from "../comopnents/snackbar";
import AuthModal from "../comopnents/AuthModal";
import { Notifyed } from "../services/Auth";
import CartQuantityControl from "../comopnents/CartQuantityControl";
import PurchaseOptionsModal from "../comopnents/PurchaseOptionsModal";
import FilterPanel from "../comopnents/FilterPanel";
import {
  buildCartPurchaseData,
  buildEnhancedPurchaseData,
  getMinimumPurchaseQuantity,
  getPurchaseQuantityStep,
  getSavedCartPurchaseData,
  shouldShowPurchaseModal,
} from "../utils/purchaseOptions";
import { isOutOfStock } from "../utils/stock";
import { getProductDisplayModel } from "../utils/productCard";
import { useProductFilters } from "../hooks/useProductFilters";
import "../styles/CategoriesPage.css";

function Shop() {
  const isLoggedIn = Boolean(localStorage.getItem("token"));
  const [searchParams] = useSearchParams();
  const subcategoryFromUrl = searchParams.get("subcategory");
  const categoryFromUrl = searchParams.get("category");
  const navigate = useNavigate();
  const location = useLocation();
  const professionCode =
    location.state?.professionCode || location.state?.profession || null;
  const professionName = location.state?.professionName || null;
  const professionLabel =
    {
      CAP: "Carpenter",
      REP: "Repair Technician",
      PLU: "Plumber",
      WEL: "Welder",
      DEM: "Demolition Worker",
    }[professionCode] || professionCode;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [professionLoading, setProfessionLoading] = useState(false);
  const [professionError, setProfessionError] = useState(null);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [wishlist, setWishlist] = useState({});
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingCartId, setPendingCartId] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [pendingPurchaseProduct, setPendingPurchaseProduct] = useState(null);
  const {
    addToCart,
    deleteCartItems,
    itemList,
    setCartQty,
    userId,
    removeFromCart,
  } = useContext(CartContext);

  const filters = useProductFilters(products);
  const { filteredProducts, maxPrice, setSelectedCategory } = filters;

  /* 🔹 LOAD PRODUCTS */
  useEffect(() => {
    if (professionCode) return;

    if (categoryFromUrl) {
      loadCategoryProducts(categoryFromUrl);
      return;
    }

    if (subcategoryFromUrl) {
      loadProducts(subcategoryFromUrl);
    }
  }, [subcategoryFromUrl, categoryFromUrl, professionCode]);

  /* 🔹 LOAD PROFESSION PRODUCTS */
  useEffect(() => {
    if (!professionCode) return;

    const fetchByProfession = async () => {
      try {
        setProfessionLoading(true);
        setProfessionError(null);
        const data = await getShopProfession(professionCode);
        const productsData = Array.isArray(data)
          ? data
          : data?.data || data?.Data || [];
        const uniqueProducts = Array.from(
          new Map(productsData.map((item) => [item.productid, item])).values(),
        );
        setProducts(uniqueProducts);
      } catch (error) {
        console.error("Profession fetch error:", error);
        setProfessionError("Failed to load products. Please try again.");
        setProducts([]);
      } finally {
        setProfessionLoading(false);
      }
    };

    fetchByProfession();
  }, [professionCode]);

  const loadProducts = async (subcategory) => {
    try {
      setLoading(true);
      const res = await getSubcategoryDetails(subcategory);
      setProducts(res.data);
    } catch (e) {
      console.error("Error loading products:", e);
    } finally {
      setLoading(false);
    }
  };

  const loadCategoryProducts = async (category) => {
    try {
      setLoading(true);

      if (category === "all") {
        const menu = await getMenu();
        const menuCategories = Array.isArray(menu?.Data) ? menu.Data : [];
        const categoryResponses = await Promise.all(
          menuCategories.map((categoryItem) =>
            getCategoryProductList(categoryItem.category).catch(() => ({
              data: [],
            })),
          ),
        );
        const allProducts = categoryResponses.flatMap((response) =>
          Array.isArray(response?.data) ? response.data : [],
        );
        const seenProductKeys = new Set();
        const uniqueProducts = allProducts.filter((product, index) => {
          const productKey =
            product?.productid ||
            product?.feeproductid ||
            product?.modelno ||
            `${product?.category || "category"}-${product?.subcategory || "subcategory"}-${index}`;

          if (seenProductKeys.has(productKey)) return false;
          seenProductKeys.add(productKey);
          return true;
        });

        setProducts(uniqueProducts);
        setSelectedCategory(null);
        return;
      }

      const res = await getCategoryProductList(category);
      setProducts(Array.isArray(res?.data) ? res.data : []);
      setSelectedCategory(category === "all" ? null : category);
    } catch (e) {
      console.error("Error loading category products:", e);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  /* 🔹 CART & WISHLIST HANDLERS */
  const addItemToCart = async (product, qty, purchaseData = null) => {
    const productId = product?.productid || product;
    if (isOutOfStock(product)) {
      await handleNotifyMe(productId);
      return;
    }

    const userId = localStorage.getItem("userId");
    if (!userId) {
      setPendingCartId({ product, productId, qty });
      setShowAuthModal(true);
      return;
    }

    try {
      const payload = {
        customerid: userId,
        productid: productId,
        qty: qty,
        stock: product?.stock,
      };
      await AddItemToCart(payload);
      addToCart(productId, qty, purchaseData);
      showSnackbar("success", "Item added to cart!");
    } catch (error) {
      console.error(
        "Add to cart error:",
        error.response?.data || error.message,
      );
      showSnackbar("error", "Failed to add item to cart.");
    }
  };

  const openPurchaseModal = (product) => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      setPendingCartId({ product });
      setShowAuthModal(true);
      return;
    }

    if (!shouldShowPurchaseModal(product)) {
      const purchaseData = buildCartPurchaseData(product, "piece");
      addItemToCart(product, purchaseData.quantity, purchaseData);
      return;
    }
    setPendingPurchaseProduct(product);
    setShowPurchaseModal(true);
  };

  const confirmPurchaseType = async (purchaseType, couponData) => {
    if (!pendingPurchaseProduct) return;
    const purchaseData = buildEnhancedPurchaseData(
      pendingPurchaseProduct,
      purchaseType,
      couponData,
    );
    await addItemToCart(
      pendingPurchaseProduct,
      purchaseData.quantity,
      purchaseData,
    );
    setShowPurchaseModal(false);
    setPendingPurchaseProduct(null);
  };

  const AddToWishList = async (productid) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) {
        setPendingCartId(productid);
        setShowAuthModal(true);
        return;
      }

      if (wishlistItems.includes(productid)) return;

      await AddItemToWishList({ productid, customerid: userId });
      setWishlistItems((prev) => [...prev, productid]);
      showSnackbar("success", "Added to wishlist!");
    } catch (error) {
      console.error("Wishlist error:", error.response?.data || error.message);
    }
  };

  const handleDeleteWishList = async (productid) => {
    try {
      const userId = localStorage.getItem("userId");
      if (!userId) return;

      await DeleteFromWishList({ customerid: userId, productid });
      setWishlist((prev) => ({ ...prev, [productid]: false }));
    } catch (err) {
      console.error("Error deleting from wishlist:", err);
    }
  };

  const handlePress = async (userId, productid, newQty) => {
    if (!userId) {
      setPendingCartId({ productId: productid, qty: newQty });
      setShowAuthModal(true);
      return;
    }

    try {
      await getProductQty({ user_id: userId, productid, qty: newQty });
      setCartQty(productid, newQty);
    } catch (error) {
      console.error("API Error:", error?.message || error);
    }
  };

  const handleDelete = async (userId, productid) => {
    try {
      await DeleteFromCart({ customerid: userId, productid });
      removeFromCart(productid);
    } catch (error) {
      console.error("API Error:", error?.message || error);
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    if (pendingCartId) {
      addItemToCart(
        pendingCartId.product || pendingCartId.productId,
        pendingCartId.qty,
      );
      setPendingCartId(null);
    }
  };

  const handleNotifyMe = async (productid) => {
    const userId = localStorage.getItem("userId");

    // If not logged in → open login modal
    if (!userId) {
      setPendingCartId(productid);
      setShowAuthModal(true);
      return;
    }

    try {
      const response = await Notifyed({
        customerid: userId,
        productid: productid,
      });

      console.log("Notify me success:", response);
      showSnackbar("success", "We will notify you when product is available");
    } catch (error) {
      console.error("Notify me error:", error.response?.data || error.message);
      showSnackbar("error", "Failed to register notify request");
    }
  };
  const getFinalPrice = (product, selectedQty) => {
    let price = product.mrp;

    if (product.discount) {
      price = price - (price * Number(product.discount)) / 100;
    }

    const bulkItem = product.discount_items
      ?.filter((item) => selectedQty >= item.qty)
      .sort((a, b) => b.qty - a.qty)[0];

    if (bulkItem?.discount) {
      price = price - (price * Number(bulkItem.discount)) / 100;
    }

    return Math.round(price);
  };

  const goToProductDetail = (productId) => {
    navigate(`/productdetail?productid=${productId}`);
  };

  return (
    <div style={{ backgroundColor: "fafafa" }}>
      <div>
        {professionCode && !loading && !professionLoading && (
          <div
            style={{
              marginBottom: 16,
              fontSize: 20,
              fontWeight: 700,
              color: "#005487",
              marginLeft: "313px",
              backgroundColor: "#fffafa",
              marginTop: "20px",
            }}
          >
            Profession: {professionName || professionLabel}
          </div>
        )}
      </div>

      <div className="shop-container">
        <FilterPanel filters={filters} />

        {/* 🔹 PRODUCT GRID */}

        <div className="product-grid">
          {(loading || professionLoading) && (
            <div className="loading-container">
              <Spin
                tip={
                  professionLoading
                    ? "Loading profession products..."
                    : "Loading"
                }
                size="large"
              />
            </div>
          )}

          {!loading && !professionLoading && professionError && (
            <div className="no-products">
              <p>{professionError}</p>
            </div>
          )}

          {!loading &&
            !professionLoading &&
            !professionError &&
            professionCode &&
            filteredProducts.length === 0 && (
              <div className="no-products">
                <p>No products found for this profession.</p>
              </div>
            )}

          {!loading &&
            !professionLoading &&
            !professionError &&
            filteredProducts.length === 0 &&
            !professionCode && (
              <div className="no-products">
                <p>No products found.</p>
              </div>
            )}
{filteredProducts
  .filter((p) => Number(p?.active) !== 0)
  .map((p) => {
            const qty =
              itemList.find((i) => String(i.productId) === String(p.productid))
                ?.count || 0;
            const minorderqty = p.minorderqty || p.dtl?.[0]?.minorderqty || 1;
            const savedPurchaseData = getSavedCartPurchaseData(p.productid);
            const purchaseType = savedPurchaseData?.purchaseType || "piece";
            const discount = Number(p.discount || p.dtl?.[0]?.discount || 0);
            const imageUrl =
              p.image1?.trim() || p.dtl?.[0]?.image1?.trim() || noimage;
            const stock = p.stock || p.dtl?.[0]?.stock || 0;
            const netrate = Number(p.netrate || p.dtl?.[0]?.netrate || 0);
            const pricing = (() => {
              const productMrp = p.mrp || p.dtl?.[0]?.mrp || 0;
              if (!productMrp) return null;
              const netrateVal = p.netrate || p.dtl?.[0]?.netrate;
              return {
                mrp: productMrp,
                netrate: Number(netrateVal || 0),
                finalPrice:
                  netrateVal ||
                  Math.round(productMrp - (productMrp * discount) / 100),
              };
            })();

            return (
              <div
                className="category-product-card category-products-home-card"
                key={p.productid || p.feeproductid || p.modelno}
              >
                <button
                  type="button"
                  className="category-products-home-image-btn"
                  onClick={() => goToProductDetail(p.productid)}
                  style={{
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    width: "100%",
                    cursor: "pointer",
                    position: "relative",
                  }}
                >
                  <div
                    className="bestsellers-actions"
                    style={{
                      position: "absolute",
                      top: "-12px",
                      left: "10px",
                      right: "10px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      zIndex: 2,
                    }}
                  >
                    {isLoggedIn && ((stock > 0 && netrate > 0) || discount > 0) && (
                      <span
                        className="discount-badge"
                        style={{
                          left: "-11px",
                          background:
                            "linear-gradient(135deg, #ff4d4d, #d90429)",
                          color: "#fff",
                          padding: "4px 2px",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: "700",
                          lineHeight: 1,
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {stock > 0 && netrate > 0
                          ? "Netrate"
                          : `${discount}% OFF`}
                      </span>
                    )}

                    <button
                      className="wishlist-btn"
                      style={{
                        position: "absolute",
                        top: "6px",
                        right: "-6px",
                        width: "40px",
                        height: "40px",
                        border: "none",
                        borderRadius: "50%",
                        background: "#fff",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        zIndex: 10,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setWishlist((prev) => {
                          const updated = !prev[p.productid];

                          if (updated) {
                            AddToWishList(p.productid);
                          } else {
                            handleDeleteWishList(p.productid);
                          }

                          return {
                            ...prev,
                            [p.productid]: updated,
                          };
                        });
                      }}
                      aria-label={
                        wishlist[p.productid]
                          ? "Remove from wishlist"
                          : "Add to wishlist"
                      }
                      type="button"
                    >
                      {wishlist[p.productid] ? (
                        <FaHeart color="#ff4d4f" size={18} />
                      ) : (
                        <FaRegHeart size={18} />
                      )}
                    </button>
                  </div>
                  <img
                    src={imageUrl}
                    alt={getProductDisplayModel(p)}
                    onError={(event) => {
                      event.currentTarget.src = noimage;
                    }}
                    style={{
                      width: "100%",
                      objectFit: "cover",
                    }}
                  />
                </button>

                <span>{p.brand || p.category}</span>

                <strong>
                  {getProductDisplayModel(p).toUpperCase()}
                </strong>

                <small>
                  {(p.subcategory || p.category)?.charAt(0).toUpperCase() +
                    (p.subcategory || p.category)?.slice(1).toLowerCase()}
                </small>
                 {isLoggedIn && <small>Min order Qty : {product.minorderqty}</small>}

                {pricing && (
                  <p className="category-products-home-price-row">
                    {Number(pricing?.mrp || 0) <= 0 ? (
                      <span
                        className="category-products-home-price"
                        style={{
                          color: "#005487",
                          fontSize: "15px",
                          fontWeight: "600",
                        }}
                      >
                        Coming Soon
                      </span>
                    ) : (
                      <>
                        {!isLoggedIn && (
                          <span
                            className="category-products-home-mrp"
                            style={{ textDecoration: "none" }}
                          >
                            MRP {pricing.mrp}
                          </span>
                        )}

                        {isLoggedIn && netrate <= 0 && (
                          <span className="category-products-home-mrp">
                            MRP {pricing.mrp}
                          </span>
                        )}

                        {isLoggedIn && (
                          <span
      className="category-product-price"
      style={{
        color: "#005487",
        fontSize: "15px",
        fontWeight: "600",
        display: "block",
        textAlign: "right",
        marginLeft: "auto",
      }}
                          >
                            Rs.{" "}
                            {Number(
                              pricing.netrate > 0
                                ? pricing.netrate
                                : pricing.finalPrice || 0,
                            ).toFixed(2)}
                          </span>
                        )}
                      </>
                    )}
                  </p>
                )}

                {stock <= 0 ? (
                  <button
                    style={{
                      width: "100%",
                      padding: "7px",
                      borderRadius: "25px",
                      border: "none",
                      fontWeight: "600",
                      backgroundColor: "#e0e0e0",
                      color: "#777777",
                      transition: "background-color 0.3s ease",
                      cursor: "pointer",
                    }}
                    onClick={() => handleNotifyMe(p.productid)}
                  >
                    Notify Me
                  </button>
                ) : qty > 0 ? (
                  <CartQuantityControl
                    qty={qty}
                    minQty={getMinimumPurchaseQuantity(p, purchaseType)}
                    step={
                      purchaseType !== "piece"
                        ? getPurchaseQuantityStep(p, purchaseType)
                        : undefined
                    }
                    stock={stock}
                    purchaseType={purchaseType}
                    product={p}
                    onChange={(newQty) =>
                      handlePress(userId, p.productid, newQty)
                    }
                    className="qty-box"
                  />
                ) : (
                  <button
                    style={{
                      width: "100%",
                      padding: "7px",
                      borderRadius: "25px",
                      border: "none",
                      fontWeight: "600",
                      backgroundColor: "#005487",
                      color: "#ffffff",
                      transition: "background-color 0.3s ease",
                      cursor: "pointer",
                    }}
                    onClick={() => openPurchaseModal(p)}
                  >
                    Add to Cart
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showAuthModal && (
        <AuthModal
          initialMode="login"
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      )}

      <PurchaseOptionsModal
        isOpen={showPurchaseModal}
        product={pendingPurchaseProduct}
        onClose={() => {
          setShowPurchaseModal(false);
          setPendingPurchaseProduct(null);
        }}
        onConfirm={confirmPurchaseType}
      />
    </div>
  );
}
const styles = {
  container: {
    background: "white",
    padding: "20px",
    borderRadius: "8px",
    color: "black",
  },
};
export default Shop;
